import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from 'react-router-dom';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, Skeleton } from '@/components/ui';
import { useAdminDashboard } from '@/hooks/useAdminDashboard';
import { useAuth } from '@/hooks/useAuth';
import {
  downloadTransactionsCSV,
  useBillingOverview,
  useBillingSummary,
  useBillingTransactions,
  type SummaryRow,
  useMyPlan,
  useVoiceAllotment,
  useVoiceSpend,
  type MyPlan,
  type TransactionItem,
  type TransactionType,
  type VoiceAllotment,
  type VoiceSpend,
} from '@/hooks/useBilling';
import { useFeature } from '@/hooks/useFeatures';
import { usePaymentMethods } from '@/hooks/usePayments';
import { api, ApiError } from '@/lib/api';
import { formatCurrency, formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Top-up modal — Stripe hosted checkout when configured (redirects to
// Stripe and back to /billing?topup=success|cancelled); support-email
// fallback while no provider is configured. We never fake charging.
// ---------------------------------------------------------------------------
const QUICK_AMOUNTS = [100, 500, 1000, 5000] as const;

interface TopupModalProps {
  open: boolean;
  onClose: () => void;
}

/** Payment method entry: `execute` methods run a real checkout flow;
 *  `info` methods render payment instructions (manual credit by ops)
 *  until their adapter ships and they graduate to `execute`. */
type TopupMethod = 'card' | 'paypal' | 'zelle' | 'crypto' | 'wire';

/** Static how-to-pay details for the informational methods. Shown to
 *  every tenant — this is public remittance info, managed here until
 *  an admin-configurable payment-settings surface exists. */
const PAY_INFO = {
  paypal: 'jafrontierconsulting@gmail.com',
  zelle: 'jafrontierconsulting@gmail.com',
  cryptoAddress: 'TAKuqAKb3MqH7SxCrEBzx4wJQZuTD5tzpj',
  cryptoNetwork: 'USDT — TRON (TRC-20)',
  wire: [
    'Bank of America',
    'JA Frontier Consulting Services LLC',
    'Account: 488138363134',
    'Routing (ACH): 111000025',
    'Routing (Wires): 026009593',
    'SWIFT: BOFAUS3N',
    '222 Broadway, New York, NY 10038',
  ],
  receiptEmail: 'Justin@softwavetelecom.com',
} as const;

function TopupModal({ open, onClose }: TopupModalProps): JSX.Element | null {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const [amount, setAmount] = useState<number>(500);
  const [method, setMethod] = useState<TopupMethod>('card');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const methods = usePaymentMethods(open);

  const stripeEnabled = methods.data?.stripe ?? false;
  // Only trust stripeEnabled once the server has actually answered.
  // A FAILED /payments/methods fetch (a transient 401/network blip)
  // must NOT be treated as "Stripe unavailable" — that wrongly pushed
  // users to the manual-credit fallback during a brief auth hiccup.
  const methodsReady = methods.isSuccess;
  const methodsFailed = methods.isError;
  const minUSD = methods.data?.min_usd ?? 10;
  const maxUSD = methods.data?.max_usd ?? 10_000;
  const amountValid = amount >= minUSD && amount <= maxUSD;

  const METHOD_CARDS: { key: TopupMethod; label: string; desc: string }[] = [
    { key: 'card', label: t('billing_method_card'), desc: t('billing_method_card_desc') },
    { key: 'paypal', label: t('billing_method_paypal'), desc: t('billing_method_manual_desc') },
    { key: 'zelle', label: t('billing_method_zelle'), desc: t('billing_method_manual_desc') },
    { key: 'crypto', label: t('billing_method_crypto'), desc: t('billing_method_manual_desc') },
    { key: 'wire', label: t('billing_method_wire'), desc: t('billing_method_manual_desc') },
  ];

  const payWithCard = async () => {
    setPayError(null);
    setPaying(true);
    try {
      const res = await api.post<{ checkout_url: string }>('/v1/payments/stripe/checkout', {
        amount_usd: amount,
      });
      // Full-page redirect to Stripe's hosted checkout; it redirects
      // back to /billing?topup=success|cancelled when done.
      window.location.href = res.checkout_url;
    } catch (err) {
      setPayError(err instanceof ApiError ? err.message : t('billing_topup_pay_error'));
      setPaying(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[480px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="display text-[16px] font-semibold text-text">{t('billing_topup_title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 grid place-items-center rounded-sm text-text-3 hover:text-text hover:bg-surface-3"
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18 18 6M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[12px] font-medium text-text-2 mb-2">
              {t('billing_topup_amount_label')}
            </label>
            <div className="flex gap-2 mb-2">
              {QUICK_AMOUNTS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAmount(v)}
                  className={cn(
                    'flex-1 num py-2 rounded-md border text-[13px] font-medium transition-colors',
                    amount === v
                      ? 'bg-volt-50 border-volt text-volt-700'
                      : 'bg-surface border-line-strong text-text-2 hover:bg-surface-2',
                  )}
                >
                  ${v.toLocaleString(lang)}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[15px] text-text-3">$</span>
              <input
                type="number"
                min={minUSD}
                max={maxUSD}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="num display text-[28px] font-semibold text-text bg-transparent border-b border-line-strong focus:border-volt-600 focus:outline-none w-[160px]"
                aria-label={t('billing_topup_amount_label')}
              />
            </div>
            <p className="text-[12px] text-text-4 mt-1">
              {t('billing_topup_range', { min: minUSD, max: maxUSD.toLocaleString(lang) })}
            </p>
          </div>

          {/* Method picker — executable methods run their checkout;
              informational ones show how-to-pay instructions and ops
              credits manually until the adapter ships. */}
          <div>
            <label className="block text-[12px] font-medium text-text-2 mb-2">
              {t('billing_method_label')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {METHOD_CARDS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { setMethod(m.key); setPayError(null); }}
                  className={cn(
                    'rounded-md border px-3 py-2.5 text-left transition-colors',
                    method === m.key
                      ? 'bg-volt-50 border-volt text-volt-700'
                      : 'bg-surface border-line-strong text-text-2 hover:bg-surface-2',
                  )}
                >
                  <span className="block text-[13px] font-medium">{m.label}</span>
                  <span className="block text-[11px] text-text-4 mt-0.5">{m.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {payError && (
            <div role="alert" className="px-3.5 py-2.5 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {payError}
            </div>
          )}

          {/* Informational panels for methods without a live adapter:
              the actual remittance details + "send the receipt" rule.
              Ops credits the balance manually on confirmation. */}
          {method !== 'card' && (
            <div className="px-3.5 py-3 bg-info-bg border border-info-bd rounded-md space-y-2">
              {method === 'paypal' && (
                <p className="text-[13px] text-text-2">
                  {t('billing_method_send_to', { method: 'PayPal' })}{' '}
                  <code className="num font-medium text-text select-all">{PAY_INFO.paypal}</code>
                </p>
              )}
              {method === 'zelle' && (
                <p className="text-[13px] text-text-2">
                  {t('billing_method_send_to', { method: 'Zelle' })}{' '}
                  <code className="num font-medium text-text select-all">{PAY_INFO.zelle}</code>
                </p>
              )}
              {method === 'crypto' && (
                <div className="text-[13px] text-text-2 space-y-1">
                  <p>{t('billing_method_crypto_send', { network: PAY_INFO.cryptoNetwork })}</p>
                  <code className="block num font-medium text-text select-all break-all bg-surface px-2 py-1.5 rounded border border-line">
                    {PAY_INFO.cryptoAddress}
                  </code>
                  <p className="text-[12px] text-warn">{t('billing_method_crypto_network_warn')}</p>
                </div>
              )}
              {method === 'wire' && (
                <div className="text-[13px] text-text-2">
                  <p className="mb-1">{t('billing_method_wire_details')}</p>
                  <div className="bg-surface px-3 py-2 rounded border border-line space-y-0.5">
                    {PAY_INFO.wire.map((line) => (
                      <p key={line} className="num text-[12.5px] text-text select-all">{line}</p>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[12.5px] text-text-3">
                {t('billing_method_receipt_note')}{' '}
                <a
                  href={`mailto:${PAY_INFO.receiptEmail}?subject=${encodeURIComponent(`Top up via ${method}: ${formatCurrency(amount, lang)}`)}`}
                  className="font-medium text-info hover:underline"
                >
                  {PAY_INFO.receiptEmail}
                </a>
              </p>
            </div>
          )}
          {method === 'card' && methods.isLoading && (
            <div className="px-3.5 py-3 bg-surface-2 border border-line rounded-md">
              <p className="text-[13px] text-text-3 leading-relaxed">{t('billing_methods_loading')}</p>
            </div>
          )}
          {method === 'card' && methodsFailed && (
            <div className="px-3.5 py-3 bg-warn-bg border border-warn-bd rounded-md">
              <p className="text-[13px] text-text-2 leading-relaxed">{t('billing_methods_load_error')}</p>
            </div>
          )}
          {method === 'card' && methodsReady && !stripeEnabled && (
            <div className="px-3.5 py-3 bg-info-bg border border-info-bd rounded-md">
              <p className="text-[13px] text-text-2 leading-relaxed">{t('billing_stripe_soon_desc')}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('billing_topup_close')}
          </Button>
          {method === 'card' && methods.isLoading ? (
            <Button disabled>{t('billing_methods_loading')}</Button>
          ) : method === 'card' && methodsFailed ? (
            // Transient fetch failure — offer a retry, NOT the manual path.
            <Button variant="secondary" onClick={() => void methods.refetch()}>
              {t('billing_methods_retry')}
            </Button>
          ) : method === 'card' && stripeEnabled ? (
            <Button onClick={() => void payWithCard()} disabled={paying || !amountValid}>
              {paying ? t('billing_topup_redirecting') : t('billing_topup_pay_card', { amount: formatCurrency(amount, lang) })}
            </Button>
          ) : (
            <Button
              onClick={() => {
                const subj = encodeURIComponent(`Top up via ${method}: ${formatCurrency(amount, lang)}`);
                const body = encodeURIComponent(
                  `Hi, I sent a ${formatCurrency(amount, lang)} top-up via ${method}. Receipt attached — please credit my account.`,
                );
                window.location.href = `mailto:${PAY_INFO.receiptEmail}?subject=${subj}&body=${body}`;
              }}
            >
              {t('billing_method_sent_receipt')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transactions CSV export — small dropdown with preset ranges.
// ---------------------------------------------------------------------------
type CSVPresetKey = '7d' | '30d' | '90d' | 'this_month' | 'last_month';

function transactionsCSVRange(key: CSVPresetKey, now: Date = new Date()): { from: string; to: string } {
  const dayMs = 24 * 60 * 60 * 1000;
  const toIso = (d: Date) => d.toISOString();
  const startOfDay = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const startOfMonth = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const today = startOfDay(now);
  const thisMonth = startOfMonth(now);
  const lastMonth = startOfMonth(
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)),
  );
  switch (key) {
    case '7d':
      return { from: toIso(new Date(today.getTime() - 7 * dayMs)), to: toIso(now) };
    case '90d':
      return { from: toIso(new Date(today.getTime() - 90 * dayMs)), to: toIso(now) };
    case 'this_month':
      return { from: toIso(thisMonth), to: toIso(now) };
    case 'last_month':
      return { from: toIso(lastMonth), to: toIso(thisMonth) };
    case '30d':
    default:
      return { from: toIso(new Date(today.getTime() - 30 * dayMs)), to: toIso(now) };
  }
}

function TransactionsCSVButton(): JSX.Element {
  const { t } = useTranslation('default');
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState<CSVPresetKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async (key: CSVPresetKey): Promise<void> => {
    setExporting(key);
    setError(null);
    try {
      await downloadTransactionsCSV(transactionsCSVRange(key));
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const presets: Array<{ key: CSVPresetKey; labelKey: string }> = [
    { key: '7d', labelKey: 'reports_preset_7d' },
    { key: '30d', labelKey: 'reports_preset_30d' },
    { key: '90d', labelKey: 'reports_preset_90d' },
    { key: 'this_month', labelKey: 'reports_preset_this_month' },
    { key: 'last_month', labelKey: 'reports_preset_last_month' },
  ];

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setOpen((v) => !v)}
        disabled={exporting !== null}
      >
        {exporting !== null ? t('reports_exporting') : t('billing_history_export_csv')}
      </Button>
      {open && (
        <>
          {/* Backdrop closes the popover on outside click. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 mt-1 z-20 w-44 bg-surface border border-line rounded-md shadow-md py-1">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => void handleExport(p.key)}
                disabled={exporting !== null}
                className="w-full text-left px-3 py-2 text-[13px] text-text hover:bg-surface-2 disabled:opacity-50"
              >
                {t(p.labelKey)}
              </button>
            ))}
          </div>
        </>
      )}
      {error && (
        <p className="absolute right-0 mt-1 text-[11px] text-danger whitespace-nowrap" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transactions table
// ---------------------------------------------------------------------------
function txIcon(type: TransactionType): JSX.Element {
  // Credits get the success-tinted up arrow, debits the subtle minus.
  const credit = type === 'deposit' || type === 'refund' || type === 'commission_in';
  return credit ? (
    <span className="w-7 h-7 rounded-full bg-success-bg border border-success-bd grid place-items-center text-success">
      <Icon.arrowUp width="14" height="14" />
    </span>
  ) : (
    <span className="w-7 h-7 rounded-full bg-surface-3 border border-line grid place-items-center text-text-3">
      <Icon.arrowDown width="14" height="14" />
    </span>
  );
}

interface TxRowProps {
  tx: TransactionItem;
  lang: string;
}

function TxRow({ tx, lang }: TxRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const credit = tx.amount > 0;
  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <td className="px-5 py-3 w-12">{txIcon(tx.type)}</td>
      <td className="px-5 py-3">
        <div className="text-text font-medium text-[13px]">
          {tx.description || t(`billing_tx_type_${tx.type}`, { defaultValue: tx.type })}
        </div>
        {tx.reference_type && (
          <div className="text-[11px] text-text-3 mono mt-0.5">
            {tx.reference_type}
            {tx.reference_id ? `:${tx.reference_id}` : ''}
          </div>
        )}
      </td>
      <td className="px-5 py-3">
        <Badge variant={credit ? 'success' : 'neutral'} className="text-[11px]">
          {t(`billing_tx_type_${tx.type}`, { defaultValue: tx.type })}
        </Badge>
      </td>
      <td className="px-5 py-3 text-text-3 whitespace-nowrap text-[12px]">
        <span title={new Date(tx.created_at).toLocaleString(lang)}>
          {formatRelativeTime(tx.created_at, lang)}
        </span>
      </td>
      <td className="px-5 py-3 num text-right whitespace-nowrap">
        <span className={cn('font-medium', credit ? 'text-success' : 'text-text')}>
          {credit ? '+' : ''}
          {formatCurrency(tx.amount, lang)}
        </span>
        <div className="text-[11px] text-text-4 mt-0.5">{formatCurrency(tx.balance_after, lang)}</div>
      </td>
    </tr>
  );
}

// SummaryTxRow renders one consolidated-view entry. Usage rollups show
// "SMS usage — Jul 24 (3 messages)" with the day's NET amount; individual
// events keep their description. Balance-after shown the same way TxRow
// does so the running column still reads naturally.
function SummaryTxRow({ row, lang }: { row: SummaryRow; lang: string }): JSX.Element {
  const { t } = useTranslation('default');
  const credit = row.amount > 0;
  const day = new Date(row.at).toLocaleDateString(lang, { month: 'short', day: 'numeric' });

  let label: string;
  if (row.kind === 'sms_usage') {
    label = t('billing_summary_sms_day', { day, count: row.item_count ?? 0 });
  } else if (row.kind === 'voice_usage') {
    label = t('billing_summary_voice_day', { day, count: row.item_count ?? 0 });
  } else {
    label = row.description || t(`billing_tx_type_${row.type}`, { defaultValue: row.type ?? '' });
  }
  const iconType = (row.kind === 'transaction' ? (row.type ?? 'adjustment') : 'sms_charge') as TransactionType;

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <td className="px-5 py-3 w-12">{txIcon(iconType)}</td>
      <td className="px-5 py-3">
        <div className="text-text font-medium text-[13px]">{label}</div>
      </td>
      <td className="px-5 py-3 text-text-3 whitespace-nowrap text-[12px]">
        <span title={new Date(row.at).toLocaleString(lang)}>
          {formatRelativeTime(row.at, lang)}
        </span>
      </td>
      <td className="px-5 py-3 num text-right whitespace-nowrap">
        <span className={cn('font-medium', credit ? 'text-success' : 'text-text')}>
          {credit ? '+' : ''}
          {formatCurrency(row.amount, lang)}
        </span>
        <div className="text-[11px] text-text-4 mt-0.5">{formatCurrency(row.balance_after, lang)}</div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// MyPlanCard — compact "your plan" widget rendered above the balance grid.
// Surfaces the plan a tenant is on PLUS who issued it, so a reseller can
// see their wholesale plan from the platform and a customer can see their
// retail plan from their reseller. Hidden entirely when the tenant has no
// plan assigned (pure pay-as-you-go) — there's no value rendering an empty
// shell on the dashboard.
// ---------------------------------------------------------------------------
interface MyPlanCardProps {
  plan: MyPlan | undefined;
  loading: boolean;
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
}

function MyPlanCard({ plan, loading, lang, t }: MyPlanCardProps): JSX.Element | null {
  if (loading) {
    return (
      <Card padding="md" className="mb-5">
        <Skeleton className="h-4 w-40" />
      </Card>
    );
  }
  if (!plan || !plan.plan_id) return null;

  const issuerLabel =
    plan.issuer_type === 'platform'
      ? t('billing_my_plan_issuer_platform', { defaultValue: 'Wholesale plan from {{name}}', name: plan.issuer_name ?? 'Platform' })
      : t('billing_my_plan_issuer_reseller', { defaultValue: 'Plan from {{name}}', name: plan.issuer_name ?? '' });

  return (
    <Card padding="md" className="mb-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 mb-1">{issuerLabel}</div>
          <div className="display text-[18px] font-semibold text-text leading-tight">
            {plan.name}
            {plan.tagline ? <span className="ml-2 text-text-3 font-normal text-[14px]">{plan.tagline}</span> : null}
          </div>
        </div>
        <div className="flex gap-6 text-[13px]">
          <div>
            <div className="text-[11px] text-text-4 uppercase tracking-[0.06em]">{t('billing_my_plan_monthly', { defaultValue: 'Monthly' })}</div>
            <div className="num font-medium text-text">{formatCurrency(plan.monthly_fee, lang)}</div>
          </div>
          <div>
            <div className="text-[11px] text-text-4 uppercase tracking-[0.06em]">{t('billing_my_plan_included', { defaultValue: 'Included' })}</div>
            <div className="num font-medium text-text">
              {plan.included_sms == null
                ? t('billing_my_plan_negotiated', { defaultValue: 'Negotiated' })
                : plan.included_sms.toLocaleString(lang)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-text-4 uppercase tracking-[0.06em]">{t('billing_my_plan_overage', { defaultValue: 'Overage / SMS' })}</div>
            <div className="num font-medium text-text">{formatCurrency(plan.overage_per_sms, lang)}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function BillingPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const overview = useBillingOverview();
  const [txView, setTxView] = useState<'summary' | 'detailed'>('summary');
  const summaryTx = useBillingSummary({ enabled: txView === 'summary' });
  const transactions = useBillingTransactions({ limit: 50 });
  const myPlan = useMyPlan();
  const { user } = useAuth();
  const isReseller = user?.tenantType === 'reseller' || user?.tenantType === 'platform';
  // Pull the reseller's subtree breakdown only when relevant —
  // /v1/admin/dashboard is gated by RequireReseller and would 403
  // for a customer tenant. The shared queryKey means the admin
  // overview page reuses this fetch (and vice versa) without re-hit.
  const adminDashboard = useAdminDashboard({ enabled: isReseller });
  const topWholesale = adminDashboard.data?.top_customers_wholesale ?? [];

  // ?topup=open (sidebar Top up button) opens the modal on arrival;
  // success|canceled are the back-from-Stripe banners (the older
  // 'cancelled' spelling is still accepted for deploy-order safety). The
  // credit itself lands via webhook — the banner is purely informational.
  const topupResult = new URLSearchParams(window.location.search).get('topup');
  const [showTopup, setShowTopup] = useState(topupResult === 'open');

  const data = overview.data;
  const isLow = data != null && data.balance < data.low_balance_threshold;

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('nav_billing')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">{t('billing_sub')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" disabled title={t('billing_invoices_coming')}>
            {t('billing_download_invoices')}
          </Button>
          <Button onClick={() => setShowTopup(true)}>
            <Icon.plus width="14" height="14" />
            {t('billing_top_up')}
          </Button>
        </div>
      </header>

      {/* Reseller-mode explainer. The same /billing page serves both
          customers (their balance with the reseller/platform they buy
          from) and resellers (their wholesale balance with the
          platform). Without context, a reseller sees "balance" and
          isn't sure whether that's their customers' money or their own
          float — the wholesale-debit transactions that PR-A introduced
          would also be confusing. Make the model explicit. */}
      {isReseller && (
        <div className="flex items-start gap-3 px-4 py-3 mb-5 bg-info-bg border border-info-bd rounded-md">
          <Icon.warn width="18" height="18" className="text-info flex-shrink-0 mt-px" />
          <div className="text-[13px] text-text-2 leading-relaxed">
            <strong className="font-semibold text-info">
              {t('billing_reseller_banner_title')}
            </strong>
            <span className="ml-1.5">{t('billing_reseller_banner_desc')}</span>
          </div>
        </div>
      )}

      {topupResult === 'success' && (
        <div role="status" className="flex items-center gap-3 px-4 py-2.5 mb-5 bg-success-bg border border-success-bd rounded-md text-[13px] text-text-2">
          <span className="font-semibold text-success">✓</span>
          {t('billing_topup_success')}
        </div>
      )}
      {(topupResult === 'canceled' || topupResult === 'cancelled') && (
        <div role="status" className="flex items-center gap-3 px-4 py-2.5 mb-5 bg-surface-2 border border-line rounded-md text-[13px] text-text-2">
          {t('billing_topup_cancelled')}
        </div>
      )}

      <MyPlanCard plan={myPlan.data} loading={myPlan.isLoading} lang={lang} t={t} />

      {isLow && (
        <div
          role="alert"
          className="flex items-center gap-3 px-4 py-2.5 mb-5 bg-warn-bg border border-warn-bd rounded-md"
        >
          <Icon.warn width="18" height="18" className="text-warn flex-shrink-0" />
          <div className="flex-1 text-[13px]">
            <strong className="font-semibold text-warn">
              {t('billing_low_banner_title')}
            </strong>
            <span className="text-text-2 ml-1.5">
              {t('billing_low_banner_desc', {
                threshold: formatCurrency(data?.low_balance_threshold ?? 0, lang),
              })}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
        {/* Balance card — span 3, big number with halo */}
        <Card padding="lg" className="lg:col-span-3 relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-20 -right-20 w-72 h-72 pointer-events-none rounded-full"
            style={{
              background: isLow
                ? 'radial-gradient(circle, var(--warn) 0%, transparent 70%)'
                : 'radial-gradient(circle, var(--volt) 0%, transparent 70%)',
              opacity: 0.15,
              filter: 'blur(40px)',
            }}
          />
          <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-3">
            {t('billing_available_balance')}
          </div>
          {overview.isLoading ? (
            <Skeleton className="h-12 w-48" />
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="num display text-[14px] text-text-3 font-medium">$</span>
              <span
                className={cn(
                  'num display text-[44px] font-semibold tracking-tight leading-none',
                  isLow ? 'text-warn' : 'text-text',
                )}
              >
                {(data?.balance ?? 0).toLocaleString(lang, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="num text-[13px] text-text-3">USD</span>
            </div>
          )}
          {data && data.credit_limit > 0 && (
            <div className="text-[12px] text-text-3 mt-2">
              {t('billing_credit_limit', { amount: formatCurrency(data.credit_limit, lang) })}
            </div>
          )}
          <div className="mt-6 pt-4 border-t border-line">
            <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-2">
              {t('billing_quick_topup')}
            </div>
            <div className="flex gap-2 flex-wrap">
              {QUICK_AMOUNTS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setShowTopup(true)}
                  className="num px-3.5 py-1.5 rounded-pill border border-line-strong text-[13px] text-text-2 hover:bg-surface-2 hover:border-text-3 transition-colors"
                >
                  ${v.toLocaleString(lang)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowTopup(true)}
                className="px-3.5 py-1.5 text-[13px] text-volt-500 font-medium hover:underline"
              >
                {t('billing_custom_amount')}
              </button>
            </div>
          </div>
        </Card>

        {/* Projection card — span 2 */}
        <Card padding="lg" className="lg:col-span-2">
          <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em] mb-3">
            {t('billing_projection_title')}
          </div>
          {overview.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-[12px] text-text-3">{t('billing_avg_per_day')}</div>
                <div className="num text-[20px] font-semibold text-text mt-0.5">
                  {formatCurrency(data?.avg_spend_per_day ?? 0, lang)}
                </div>
              </div>
              <div>
                <div className="text-[12px] text-text-3">{t('billing_runs_out')}</div>
                <div className="num text-[20px] font-semibold text-text mt-0.5">
                  {data?.runs_out_in_days != null
                    ? t('billing_runs_out_value', {
                        days: Math.round(data.runs_out_in_days).toLocaleString(lang),
                      })
                    : t('billing_runs_out_unknown')}
                </div>
              </div>
              <div>
                <div className="text-[12px] text-text-3">{t('billing_spent_7d')}</div>
                <div className="num text-[20px] font-semibold text-text mt-0.5">
                  {formatCurrency(data?.spend_last_7d ?? 0, lang)}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Top customers by wholesale spend (resellers only). Renders only
          after the admin/dashboard fetch returns rows; hidden during the
          initial load + when the subtree has zero spend so the page
          doesn't show an empty card to a brand-new reseller. */}
      {isReseller && topWholesale.length > 0 && (
        <Card padding="none" className="mb-6">
          <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
            <div>
              <h2 className="display text-[16px] font-semibold text-text">
                {t('billing_top_wholesale_title')}
              </h2>
              <p className="text-[12px] text-text-3 mt-0.5">
                {t('billing_top_wholesale_sub')}
              </p>
            </div>
            <Link
              to="/admin/customers"
              className="text-[12px] text-volt-500 font-medium hover:underline whitespace-nowrap"
            >
              {t('billing_top_wholesale_view_all')}
            </Link>
          </div>
          <ul>
            {topWholesale.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-3 px-5 py-3 border-b border-line-soft last:border-0 hover:bg-surface-2"
              >
                <Link
                  to={`/admin/customers/${c.id}`}
                  className="flex-1 min-w-0 flex items-center gap-3"
                >
                  <span
                    aria-hidden="true"
                    className="w-8 h-8 rounded-md grid place-items-center text-volt-ink text-[12px] font-bold flex-shrink-0"
                    style={{
                      background:
                        'linear-gradient(135deg, var(--volt) 0%, var(--volt-600) 100%)',
                    }}
                  >
                    {c.name.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] text-text font-medium truncate">{c.name}</div>
                    <div className="text-[11.5px] text-text-3 mt-0.5">
                      {c.country && <span className="mono mr-1.5">{c.country}</span>}
                      {t('billing_top_wholesale_sent', {
                        count: Number(c.sent),
                        formatted: c.sent.toLocaleString(lang),
                      })}
                    </div>
                  </div>
                </Link>
                <div className="num text-right whitespace-nowrap">
                  <div className="text-[14px] font-semibold text-text">
                    {formatCurrency(c.wholesale_spend, lang)}
                  </div>
                  <div className="text-[11px] text-text-4">
                    {t('billing_top_wholesale_30d')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Voice allotment widget — visible only when the tenant is on a
          plan that bundles voice minutes. Lives above the spend section
          because "how many free min do I have left" is a more frequent
          question than "what did I spend last month." */}
      <VoiceAllotmentSection lang={lang} />

      {/* Voice spend (30d) — only when voice is enabled for the tenant.
          Backend returns zero rows for tenants with no voice activity, so
          a freshly-onboarded voice customer won't see noise. */}
      <VoiceSpendSection lang={lang} />

      {/* Transactions. Summary (default) consolidates usage into one net
          row per day and hides internal reserve/refund pairs; Detailed
          is the raw ledger. Billing is real-time either way — this is
          presentation only. */}
      <Card padding="none">
        <div className="px-5 py-4 border-b border-line flex items-start justify-between gap-3">
          <div>
            <h2 className="display text-[16px] font-semibold text-text">
              {t('billing_history_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">
              {txView === 'summary'
                ? t('billing_history_summary_sub')
                : t('billing_history_count', {
                    total: (transactions.data?.total ?? 0).toLocaleString(lang),
                  })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-line-strong overflow-hidden">
              {(['summary', 'detailed'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setTxView(v)}
                  className={cn(
                    'px-3 py-1.5 text-[12px] font-medium transition-colors',
                    txView === v ? 'bg-volt-50 text-volt-700' : 'bg-surface text-text-3 hover:bg-surface-2',
                  )}
                >
                  {t(`billing_view_${v}`)}
                </button>
              ))}
            </div>
            <TransactionsCSVButton />
          </div>
        </div>
        {txView === 'summary' ? (
          summaryTx.isLoading ? (
            <div className="p-5 space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (summaryTx.data?.items.length ?? 0) === 0 ? (
            <div className="p-10 text-center">
              <p className="text-[14px] text-text-2 font-medium">{t('billing_history_empty_title')}</p>
              <p className="text-[13px] text-text-3 mt-1">{t('billing_history_empty_sub')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                    <th className="px-5 py-2.5 font-medium w-12"></th>
                    <th className="px-5 py-2.5 font-medium">{t('billing_col_description')}</th>
                    <th className="px-5 py-2.5 font-medium">{t('billing_col_when')}</th>
                    <th className="px-5 py-2.5 font-medium text-right">{t('billing_col_amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryTx.data?.items.map((row) => (
                    <SummaryTxRow key={`${row.kind}-${row.at}`} row={row} lang={lang} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : transactions.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (transactions.data?.items.length ?? 0) === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('billing_history_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('billing_history_empty_sub')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium w-12"></th>
                  <th className="px-5 py-2.5 font-medium">{t('billing_col_description')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('billing_col_type')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('billing_col_when')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('billing_col_amount')}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.data?.items.map((tx) => (
                  <TxRow key={tx.id} tx={tx} lang={lang} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <TopupModal open={showTopup} onClose={() => setShowTopup(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice allotment — "X / 500 min used" widget for customers on a plan
// that bundles voice minutes. Backend returns an empty payload when the
// tenant has no allotment (no plan or plan doesn't include voice); we
// hide the section in that case so non-voice tenants don't see the
// scaffolding.
// ---------------------------------------------------------------------------
function VoiceAllotmentSection({ lang }: { lang: string }): JSX.Element | null {
  const { t } = useTranslation('default');
  const voiceOn = useFeature('voice');
  const allotment = useVoiceAllotment({ enabled: voiceOn });

  if (!voiceOn) return null;
  if (allotment.isLoading) return null;
  if (!allotment.data || allotment.data.included_minutes <= 0) return null;

  return <VoiceAllotmentCard lang={lang} t={t} data={allotment.data} />;
}

interface VoiceAllotmentCardProps {
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  data: VoiceAllotment;
}

function VoiceAllotmentCard({ lang, t, data }: VoiceAllotmentCardProps): JSX.Element {
  const pct = Math.round(data.used_ratio * 100);
  // Colour the progress bar by consumption — green up to 70%, amber to
  // 90%, red beyond. Same thresholds the dashboard balance tile uses
  // for "running low" signaling.
  const tone = pct < 70 ? 'volt' : pct < 90 ? 'warn' : 'danger';
  const fillColor =
    tone === 'volt'
      ? 'var(--volt-500)'
      : tone === 'warn'
        ? 'var(--warn)'
        : 'var(--danger)';
  // Period is 'YYYY-MM' from the API. Render as "May 2026" for the
  // header, and compute the next-month-1st as the renewal date.
  const periodLabel = formatPeriodLabel(data.period, lang);
  const renewsOn = formatRenewsOn(data.period, lang);

  return (
    <Card padding="none" className="mb-6">
      <div className="px-5 py-4 border-b border-line flex items-start justify-between gap-3">
        <div>
          <h2 className="display text-[16px] font-semibold text-text">
            {t('billing_voice_allotment_title', { period: periodLabel })}
          </h2>
          {data.plan_name && (
            <p className="text-[12px] text-text-3 mt-0.5">{data.plan_name}</p>
          )}
        </div>
        <div className="text-right whitespace-nowrap">
          <div className="num text-[14px] text-text font-semibold">
            {t('billing_voice_allotment_count', {
              used: data.minutes_used.toLocaleString(lang),
              total: data.included_minutes.toLocaleString(lang),
            })}
          </div>
          <div className="text-[11px] text-text-4 mt-0.5">
            {t('billing_voice_allotment_remaining', {
              remaining: data.minutes_remaining.toLocaleString(lang),
            })}
          </div>
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
          <div
            className="h-full transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%`, background: fillColor }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11.5px] text-text-4">
          <span>{t('billing_voice_allotment_pct', { pct })}</span>
          <span>{t('billing_voice_allotment_renews', { date: renewsOn })}</span>
        </div>
      </div>
    </Card>
  );
}

// formatPeriodLabel turns 'YYYY-MM' into a localized "May 2026"-style
// string. Falls back to the raw period when parsing fails so the UI
// always shows something useful.
function formatPeriodLabel(period: string, lang: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(date);
}

// formatRenewsOn returns a short "Jun 1" for the 1st of the month
// AFTER the current period. Operator-readable; same locale as the rest
// of the page.
function formatRenewsOn(period: string, lang: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return '';
  const next = new Date(Number(m[1]), Number(m[2]), 1); // month index = current+1 since JS months are 0-based and we use M-1
  return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(next);
}

// ---------------------------------------------------------------------------
// Voice spend (30d) — gated on the voice feature flag so non-voice tenants
// never see the section. Skips render when the tenant has zero voice
// activity in the window so a brand-new voice customer doesn't get a wall
// of zeros.
// ---------------------------------------------------------------------------
function VoiceSpendSection({ lang }: { lang: string }): JSX.Element | null {
  const { t } = useTranslation('default');
  const voiceOn = useFeature('voice');
  const spend = useVoiceSpend({ enabled: voiceOn });

  if (!voiceOn) return null;
  if (spend.isLoading) return null;
  if (!spend.data || spend.data.billable_calls_30d === 0) return null;

  return <VoiceSpendCard lang={lang} t={t} data={spend.data} />;
}

interface VoiceSpendCardProps {
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  data: VoiceSpend;
}

function VoiceSpendCard({ lang, t, data }: VoiceSpendCardProps): JSX.Element {
  const minutes = Math.round(data.billed_seconds_30d / 60);
  return (
    <Card padding="none" className="mb-6">
      <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
        <div>
          <h2 className="display text-[16px] font-semibold text-text">
            {t('billing_voice_spend_title')}
          </h2>
          <p className="text-[12px] text-text-3 mt-0.5">
            {t('billing_voice_spend_sub')}
          </p>
        </div>
        <Link
          to="/voice/campaigns"
          className="text-[12px] text-volt-500 font-medium hover:underline whitespace-nowrap"
        >
          {t('billing_voice_spend_view_campaigns')}
        </Link>
      </div>

      {/* Only real spend (net). The gross charges + refunds are reserve
          churn — showing them made customers think they'd spent ~10x their
          actual cost. See GetBillingOverview for the same net treatment. */}
      <div className="grid grid-cols-2 gap-px bg-line">
        <VoiceMetric
          label={t('billing_voice_metric_net')}
          value={formatCurrency(data.net_spend_30d, lang)}
          emphasize
        />
        <VoiceMetric
          label={t('billing_voice_metric_calls')}
          value={`${data.billable_calls_30d.toLocaleString(lang)} · ${minutes.toLocaleString(lang)} min`}
        />
      </div>

      {data.by_card.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead className="text-text-4 text-left bg-surface-2/40">
            <tr>
              <th className="px-5 py-2 font-medium">
                {t('billing_voice_table_card')}
              </th>
              <th className="px-5 py-2 font-medium tabular-nums text-right">
                {t('billing_voice_table_calls')}
              </th>
              <th className="px-5 py-2 font-medium tabular-nums text-right">
                {t('billing_voice_table_minutes')}
              </th>
              <th className="px-5 py-2 font-medium tabular-nums text-right">
                {t('billing_voice_table_net')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.by_card.map((row, i) => {
              const net = row.charges - row.refunds;
              const rowMinutes = Math.round(row.bill_seconds / 60);
              return (
                <tr key={i} className="border-t border-line">
                  <td className="px-5 py-2 text-text">{row.card_name}</td>
                  <td className="px-5 py-2 text-text-3 tabular-nums text-right">
                    {row.calls.toLocaleString(lang)}
                  </td>
                  <td className="px-5 py-2 text-text-3 tabular-nums text-right">
                    {rowMinutes.toLocaleString(lang)}
                  </td>
                  <td className="px-5 py-2 text-text font-medium tabular-nums text-right">
                    {formatCurrency(net, lang)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function VoiceMetric({
  label,
  value,
  tone,
  emphasize,
}: {
  label: string;
  value: string;
  tone?: 'success';
  emphasize?: boolean;
}): JSX.Element {
  return (
    <div className="bg-surface px-5 py-4">
      <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 num',
          emphasize ? 'text-[20px] font-semibold text-text' : 'text-[16px] text-text-2',
          tone === 'success' && 'text-success',
        )}
      >
        {value}
      </div>
    </div>
  );
}
